import { useState } from 'react';
import { Check, Copy, RefreshCw } from 'lucide-react';
import {
  CLIENT_ROLES,
  VENDOR_ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  isVendorRole,
  type Role,
} from '@rundown/policy';
import { Pill } from '../../../../components';
import { useVendorName } from '../../../workspace/useWorkspace';
import { avatarColour, initialOf } from '../../../../lib/avatar';
import { cx } from '../../../../lib/cx';
import { PASSWORD_POLICY } from '../../../auth/api';
import type { Grade, Membership } from '../../types';

export const INPUT_CLASS =
  'h-9 w-full min-w-0 rounded-md border border-line-stronger bg-base px-2.5 fs-13 text-ink ' +
  'placeholder:text-faint focus:border-accent focus:outline-none disabled:opacity-50';

export const LABEL_CLASS = 'fs-12 font-medium text-ink-soft';

// Re-exported, not re-derived. @rundown/policy already publishes both lists and
// `isVendorRole`; filtering ROLES by a string prefix here was a third
// definition of the same split, in the screen that ASSIGNS roles — the one
// place it most needs to agree with the server.
export { CLIENT_ROLES, VENDOR_ROLES };

/** The complement of `isVendorRole`, so the two cannot disagree. */
export function isClientRole(role: Role): boolean {
  return !isVendorRole(role);
}

export function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  return (
    <span
      aria-hidden
      style={{ width: `${size / 16}rem`, height: `${size / 16}rem`, fontSize: `${(size * 0.42) / 16}rem` }}
      className={cx(
        'inline-flex flex-none items-center justify-center rounded-full font-semibold text-white',
        avatarColour(name),
      )}
    >
      {initialOf(name)}
    </span>
  );
}

/**
 * One status per person, in the order that matters to whoever reads the list.
 * A suspended account outranks an active seat: the seat is untouched, but the
 * person cannot get in, and it is the person a roster is read for.
 */
export function StatusPill({ membership }: { membership: Membership }) {
  if (membership.accountSuspended) return <Pill tone="danger">Suspended</Pill>;
  if (!membership.active) return <Pill tone="neutral">Removed</Pill>;
  return <Pill tone="success">Active</Pill>;
}

// --- Role picker ---------------------------------------------------------------

/**
 * Roles as choices with their meaning beside them, grouped by side of the
 * relationship — picking between five near-identical names in a native select
 * was the easiest place on the old screen to grant the wrong thing.
 */
export function RolePicker({
  value,
  onChange,
  disabled = false,
  name,
}: {
  value: Role;
  onChange: (role: Role) => void;
  disabled?: boolean;
  name: string;
}) {
  const vendorName = useVendorName();
  const group = (label: string, roles: readonly Role[]) => (
    <div className="flex flex-col gap-1.5">
      <span className="fs-11 font-medium tracking-wide text-faint uppercase">{label}</span>
      {roles.map((role) => {
        const selected = role === value;
        return (
          <label
            key={role}
            className={cx(
              'flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 transition-colors',
              selected ? 'border-accent bg-accent-tint-soft' : 'border-line hover:border-line-stronger',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <input
              type="radio"
              name={name}
              value={role}
              checked={selected}
              disabled={disabled}
              onChange={() => onChange(role)}
              className="mt-[0.1875rem] accent-accent"
            />
            <span className="min-w-0">
              <span className="block fs-13 font-medium text-ink">{ROLE_LABELS[role]}</span>
              <span className="block fs-11.5 leading-snug text-muted">{ROLE_DESCRIPTIONS[role]}</span>
            </span>
          </label>
        );
      })}
    </div>
  );

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {group('Client', CLIENT_ROLES)}
      {group(`${vendorName} team`, VENDOR_ROLES)}
    </div>
  );
}

// --- Password ------------------------------------------------------------------

const PASSWORD_CHECKS: readonly { readonly label: string; readonly test: (value: string) => boolean }[] = [
  {
    label: `${PASSWORD_POLICY.minLength}+ characters`,
    test: (value) => value.length >= PASSWORD_POLICY.minLength,
  },
  { label: 'Uppercase letter', test: (value) => /[A-Z]/.test(value) },
  { label: 'Number', test: (value) => /[0-9]/.test(value) },
  { label: 'Symbol', test: (value) => /[^A-Za-z0-9]/.test(value) },
];

export function passwordMeetsPolicy(value: string): boolean {
  return PASSWORD_CHECKS.every((check) => check.test(value));
}

/** No 0/O, 1/l/I: this password is read out or retyped by a person. */
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const DIGITS = '23456789';
const SYMBOLS = '!@#$%&*?-+';

function randomIndex(max: number): number {
  // Rejection sampling, so no character is likelier than another.
  const limit = Math.floor(0x1_0000_0000 / max) * max;
  const buffer = new Uint32Array(1);
  do {
    crypto.getRandomValues(buffer);
  } while (buffer[0]! >= limit);
  return buffer[0]! % max;
}

function pick(chars: string): string {
  return chars[randomIndex(chars.length)]!;
}

/** 16 characters, always satisfying the policy — one from each class, the rest from all of them, shuffled. */
export function generatePassword(length = 16): string {
  const all = UPPER + LOWER + DIGITS + SYMBOLS;
  const chars = [pick(UPPER), pick(LOWER), pick(DIGITS), pick(SYMBOLS)];
  while (chars.length < length) chars.push(pick(all));
  for (let index = chars.length - 1; index > 0; index -= 1) {
    const swap = randomIndex(index + 1);
    [chars[index], chars[swap]] = [chars[swap]!, chars[index]!];
  }
  return chars.join('');
}

export function useCopy(): { copied: boolean; copy: (text: string) => void } {
  const [copied, setCopied] = useState(false);
  return {
    copied,
    copy: (text) => {
      void navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        })
        .catch(() => undefined);
    },
  };
}

/**
 * Plain text, not masked: the admin has to pass this on, so hiding it from the
 * one person who must read it helps nobody. The requirements are shown as a
 * live checklist rather than a sentence under a greyed-out button, which is
 * what made the old form look broken.
 */
export function PasswordInput({
  value,
  onChange,
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}) {
  const { copied, copy } = useCopy();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <input
          type="text"
          autoComplete="off"
          spellCheck={false}
          autoFocus={autoFocus}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Type one or generate"
          aria-label="Password"
          className={cx(INPUT_CLASS, 'font-mono')}
        />
        <button
          type="button"
          onClick={() => onChange(generatePassword())}
          className="inline-flex h-9 flex-none items-center gap-1.5 rounded-md border border-line-stronger bg-base px-2.5 fs-12.5 font-medium text-ink hover:bg-surface-2"
        >
          <RefreshCw size={13} />
          Generate
        </button>
        <button
          type="button"
          onClick={() => copy(value)}
          disabled={value === ''}
          aria-label="Copy password"
          title="Copy password"
          className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-md border border-line-stronger bg-base text-muted hover:bg-surface-2 hover:text-ink disabled:opacity-40"
        >
          {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
        </button>
      </div>

      <ul className="flex flex-wrap gap-x-3 gap-y-1">
        {PASSWORD_CHECKS.map((check) => {
          const ok = check.test(value);
          return (
            <li
              key={check.label}
              className={cx('inline-flex items-center gap-1 fs-11.5', ok ? 'text-success' : 'text-faint')}
            >
              <span
                className={cx(
                  'inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border',
                  ok ? 'border-success bg-success text-white' : 'border-line-heavy',
                )}
              >
                {ok && <Check size={9} strokeWidth={3} />}
              </span>
              {check.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function MustChangeToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 fs-12 text-ink-soft">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-[0.125rem] accent-accent"
      />
      <span>
        Ask them to choose a new password when they first sign in
        <span className="block fs-11.5 text-muted">
          Recommended — you won't know their password after that.
        </span>
      </span>
    </label>
  );
}

/**
 * The handover, shown once. Nothing here is stored where it can be read back,
 * so this card is the only copy — hence "copy everything" as one click.
 */
export function CredentialsCard({ email, password }: { email: string; password: string }) {
  const { copied, copy } = useCopy();
  const text = `Sign in to Rundown at ${window.location.origin}\nEmail: ${email}\nPassword: ${password}`;

  return (
    <div className="rounded-lg border border-warn-border bg-warn-tint p-3">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 fs-12.5">
        <dt className="text-muted">Email</dt>
        <dd className="min-w-0 truncate text-ink">{email}</dd>
        <dt className="text-muted">Password</dt>
        <dd className="min-w-0 font-mono font-semibold break-all text-ink">{password}</dd>
      </dl>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="fs-11.5 text-warn">Shown only once — copy it before closing.</p>
        <button
          type="button"
          onClick={() => copy(text)}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line-stronger bg-base px-3 fs-12.5 font-medium text-ink hover:bg-surface-2"
        >
          {copied ? <Check size={13} className="text-success" /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy sign-in details'}
        </button>
      </div>
    </div>
  );
}

export function ErrorText({ message }: { message: string | null | undefined }) {
  if (message === null || message === undefined) return null;
  return (
    <p role="alert" className="rounded-md bg-danger-tint px-2.5 py-1.5 fs-12 text-danger">
      {message}
    </p>
  );
}

/**
 * Grades, in the words the screen uses.
 *
 * There is no separate engineer record any more, so a grade is an attribute of
 * a person and these labels travel with the People screen rather than with a
 * roster of their own.
 */
export const GRADE_LABELS: Readonly<Record<Grade, string>> = {
  junior: 'Junior',
  mid_senior: 'Mid/Senior',
  senior: 'Senior',
};

/** The factor each grade starts at, mirroring DEFAULT_FACTOR_BP in people.service.ts. */
export const DEFAULT_FACTOR_BP: Readonly<Record<Grade, number>> = {
  junior: 2_500,
  mid_senior: 10_000,
  senior: 12_000,
};

/**
 * The delivery fields, for whoever does the work.
 *
 * Shown only for a Noorix role, because that is the only place they mean
 * anything: a client requester has no grade and no share of the week, and
 * rendering empty inputs for them would suggest otherwise. Selecting a grade
 * fills the factor with that grade's default rather than leaving a required
 * number blank — the pair has to move together (the database refuses a
 * half-set pair), and nobody setting someone up as "Senior" has an opinion
 * about basis points yet.
 */
export function DeliveryFields({
  grade,
  billingFactorBp,
  allocatedHours,
  onGrade,
  onFactor,
  onAllocated,
  disabled = false,
}: {
  grade: Grade | null;
  billingFactorBp: number | null;
  allocatedHours: number;
  onGrade: (grade: Grade | null) => void;
  onFactor: (bp: number | null) => void;
  onAllocated: (hours: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <label className="flex min-w-0 flex-col gap-1.5">
        <span className={LABEL_CLASS}>Grade</span>
        <select
          value={grade ?? ''}
          disabled={disabled}
          onChange={(event) => {
            const next = event.target.value === '' ? null : (event.target.value as Grade);
            onGrade(next);
            onFactor(next === null ? null : DEFAULT_FACTOR_BP[next]);
          }}
          className={INPUT_CLASS}
        >
          <option value="">No grade — cannot log time</option>
          {(Object.keys(GRADE_LABELS) as Grade[]).map((value) => (
            <option key={value} value={value}>
              {GRADE_LABELS[value]}
            </option>
          ))}
        </select>
      </label>

      <label className="flex min-w-0 flex-col gap-1.5">
        <span className={LABEL_CLASS}>Billing factor</span>
        <input
          type="number"
          step={100}
          min={0}
          value={billingFactorBp ?? ''}
          disabled={disabled || grade === null}
          placeholder="—"
          onChange={(event) => onFactor(event.target.value === '' ? null : Number(event.target.value))}
          className={INPUT_CLASS}
        />
        <span className="fs-11 text-faint">
          Basis points; 10000 = ×1.0. Frozen onto each hour when it is logged.
        </span>
      </label>

      <label className="flex min-w-0 flex-col gap-1.5">
        <span className={LABEL_CLASS}>Hours on this client</span>
        <input
          type="number"
          step={1}
          min={0}
          max={168}
          value={allocatedHours}
          disabled={disabled}
          onChange={(event) => onAllocated(Number(event.target.value))}
          className={INPUT_CLASS}
        />
        <span className="fs-11 text-faint">Their week is shared across clients.</span>
      </label>
    </div>
  );
}
